<script lang="ts">
export default {
  name: 'CompaniesAnalysisQscasDetail',
}
</script>

<script setup lang="ts">
import DOMPurify from 'dompurify'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { syntaxHighlight } from '../../../../modules/syntaxHighlight'
import ChartRadar from '../../../../components/ChartRadar.vue'
import Loading from '../../../../components/Loading.vue'
import CompaniesAnalysisTable from '../../../../components/CompaniesAnalysis/Table.vue'
import Star from '../../../../components/Star.vue'
import Icon from 'src/biz_spa/components/Icon.vue'
import { KkSiteIcon } from '../../../../components/base'
import moment from 'moment'
import { useCompaniesNotAvailable } from 'src/biz_spa/composables'
import { useQSCASentences } from 'src/biz_spa/domains/analysis/composables'
import CompaniesAnalysisLayout from 'src/biz_spa/layouts/CompaniesAnalysisLayout.vue'
import CompaniesAnalysisQscasHeader from 'src/biz_spa/components/CompaniesAnalysis/Qscas/Header.vue'
import KkSentencesModal from 'src/biz_spa/domains/analysis/components/KkSentencesModal/index.vue'
import type { CrossAnalysisType, QscaDataTypes, QSCACategory } from 'src/biz_spa/types'

type QSCAQuery = {
  start_time: string
  end_time: string
  display_competitor: boolean
  analysis_axis: string
  analysis_axis_id?: number
  cross_analysis_axis?: string
  cross_analysis_axis_id?: number
}
const route = useRoute()
const store = useStore()
const { t } = useI18n()
const companyUuid = computed(() => route.params.company_uuid)
const isVisibleSentencesModal = ref(false)
const qscaCategory = ref<QSCACategory>('QUALITY')

const providedFeaturesCompetitor = computed(() => store.getters['companiesCompany/providedFeaturesCompetitor'])
const dataTypes = computed<QscaDataTypes>(() => store.getters['companiesAnalysisQscas/dataTypes'])
const crossSearchFields = computed(() => store.getters['companiesAnalysisQscas/crossSearchFields'])
const startTime = computed<string>(() => store.getters['companiesAnalysisQscas/startTime'])
const endTime = computed<string>(() => store.getters['companiesAnalysisQscas/endTime'])
const showMoreThanEditorAuthority = computed(() => store.getters['companiesAccessibleApi/showMoreThanEditorAuthority'])
const providedAiReviewSentencesAnalysis = computed<boolean>(() => store.getters['companiesCompany/providedAiReviewSentencesAnalysis'])
const competitorMode = computed<boolean>(() => store.state.companiesAnalysisQsca.competitorMode)
const qscas = computed(() => store.state.companiesAnalysisQsca.qscas)
const loading = computed<boolean>(() => store.state.companiesAnalysisQsca.loading)
const storeReviews = computed(() => store.state.companiesAnalysisQsca.storeReviews)
const competitorReviews = computed(() => store.state.companiesAnalysisQsca.competitorReviews)
const allOnly = computed(() => qscas.value.length === 1)
const allQsca = computed(() => qscas.value[0])
const qsca = computed(() => allOnly.value ? qscas.value[0] : qscas.value[1])
const pageType = computed<CrossAnalysisType>(() => ({ id: route.query.analysis_axis as string, name: '', fields: route.query.analysis_axis as string}))
const crossSearchField = computed<CrossAnalysisType>(() => ({ id: route.query.cross_analysis_axis as string, name: '', fields: route.query.cross_analysis_axis as string}))
const { notAvailableType } = useCompaniesNotAvailable(crossSearchField, pageType)
const {
  isLoading: isSentencesLoading,
  positiveSentences,
  negativeSentences,
  readSentences,
} = useQSCASentences()
const displayReviews = computed(() => showCompetitorReviews.value ? competitorReviews.value : storeReviews.value)
const query = computed<QSCAQuery>(() => ({
  start_time: startTime.value,
  end_time: endTime.value,
  display_competitor: competitorMode.value,
  analysis_axis: route.query.analysis_axis as string,
  analysis_axis_id: Number(route.query.analysis_axis_id) ,
  cross_analysis_axis: route.query.cross_analysis_axis as string,
  cross_analysis_axis_id: Number(route.query.cross_analysis_axis_id)
}))

const showCompetitorReviews = ref(false)

const readAnalysisQsca = (query: QSCAQuery) => store.dispatch('companiesAnalysisQsca/readAnalysisQsca', query)
const changeCompetitorMode = (mode: boolean) => store.dispatch('companiesAnalysisQsca/changeCompetitorMode', mode)
const changeProvidedAiReviewSentencesAnalysis = (value: boolean) => store.dispatch('companiesAnalysisQsca/changeProvidedAiReviewSentencesAnalysis', value)
const setSelectedYear = (year: number) => store.dispatch('companiesAnalysisQscas/setSelectedYear', year)
const setSelectedMonth = (month: number) => store.dispatch('companiesAnalysisQscas/setSelectedMonth', month)
const setSelectedPeriod = (period: number) => store.dispatch('companiesAnalysisQscas/setSelectedPeriod', period)

// MEMO: 「0」を「-」に変換されるのを防ぐために設定せざるを得ない
const valueFormatter = (value: number) => value

const comment = ({text, keywords}: { text: string, keywords: string[]}) => {
  return syntaxHighlight(text, keywords)
}

const sanitize = (html: Node) => {
  return DOMPurify.sanitize(html)
}

const formatDate = (date: string) => {
  return moment(date).format(t('format.datetime.default'))
}

const switchCompetitorReviews = () => {
  if (!competitorMode.value) return
  showCompetitorReviews.value = !showCompetitorReviews.value
}

const handleChangeCompetitorMode = () => {
  changeCompetitorMode(!competitorMode.value)
  readAnalysisQsca(query.value)
}

const handleChangeDateRange = ({ value, type }: { value: string; type: string }) => {
  switch (type) {
  case 'period':
    setSelectedPeriod(Number(value))
    break
  case 'month':
    setSelectedMonth(Number(value))
    break
  case 'year':
    setSelectedYear(Number(value))
    break
  }
  readAnalysisQsca(query.value)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const chartData = (data: any) => {
  const labels = dataTypes.value.map(v => v.name)
  const datasets = [
    {
      label: data.fields.map((v: CrossAnalysisType) => v.name).join('/'),
      data: dataTypes.value.map(v => data[v.id]),
      borderColor: '#E9446A',
      backgroundColor: 'transparent',
    }
  ]

  if (allQsca.value) {
    datasets.push({
      label: allQsca.value.fields.map((v: CrossAnalysisType) => v.name).join('/'),
      data: dataTypes.value.map(v => allQsca.value[v.id]),
      borderColor: '#bbbbbb',
      backgroundColor: 'transparent',
    })
  }

  return { labels, datasets }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const competitorChartData = (data: any, competitor: any) => {
  const labels = dataTypes.value.map(v => v.name)
  const datasets = [
    {
      label: data.fields.map((v: CrossAnalysisType) => v.name).join('/'),
      data: dataTypes.value.map(v => data[v.id]),
      borderColor: '#E9446A',
      backgroundColor: 'transparent'
    },
    {
      label: competitor.fields.map((v: CrossAnalysisType) => v.name).join('/'),
      data: dataTypes.value.map(v => competitor[v.id]),
      borderColor: '#46555F',
      backgroundColor: 'transparent'
    },
  ]

  return { labels, datasets }
}

const modifiedNotAvailableType = computed(() => {
  if (!competitorMode.value && notAvailableType.value === 'emptyCompetitor') return null
  return notAvailableType.value
})

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: true,
  scales: {
    r: {
      suggestedMin: 0,
      suggestedMax: 100,
      ticks: {
        stepSize: 20
      },
    },
  },
}))

const openSentenceModal = async ({ category, uuids, isCompetitor }: { category: QSCACategory; uuids: string[], isCompetitor: boolean }) => {
  qscaCategory.value = category
  isVisibleSentencesModal.value = true
  await readSentences({
    category,
    startDate: moment(startTime.value),
    endDate: moment(endTime.value),
    storeUuids: !isCompetitor ? uuids : undefined,
    competitorIds: isCompetitor ? uuids : undefined,
  })
}
const initialized = async () => {
  changeCompetitorMode(false)
  changeProvidedAiReviewSentencesAnalysis(providedAiReviewSentencesAnalysis.value)
  readAnalysisQsca(query.value)
}

initialized()
</script>


<template>
  <CompaniesAnalysisLayout
    :not-available-type="modifiedNotAvailableType"
    :company-uuid="companyUuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <template #header>
      <CompaniesAnalysisQscasHeader
        @on-date-range-changed="handleChangeDateRange"
      />
    </template>
    <template #graph>
      <Loading v-show="loading" />
      <div v-if="!loading" class="chartArea">
        <div class="chartArea__header">
          <div class="analysisTarget">
            {{ $t('pages.companies.analysis.qscas.analysisTarget') }}: <span class="u-fwB">{{ qsca.fields.map((v: CrossAnalysisType) => v.name).join('/') }}</span>
          </div>
          <div v-if="providedFeaturesCompetitor">
            <div class="c-radio__item">
              <input
                :id="'competitor_radio_btn'"
                :checked="competitorMode"
                class="c-radio__input"
                type="checkbox"
                :disabled="loading"
                @change="handleChangeCompetitorMode"
              >
              <label class="c-radio__label" :for="'competitor_radio_btn'">{{ $t('pages.companies.analysis.reviews.displayCompetitorData') }}</label>
            </div>
          </div>
        </div>
        <div class="chartArea__main">
          <div class="chart">
            <ChartRadar
              :chart-data="chartData(qsca)"
              :options="chartOptions"
            />
          </div>
          <div v-if="competitorMode" class="competitorCharts">
            <div class="competitorCharts__header">
              {{ $t('pages.companies.analysis.qscas.competitorCharts.header', { count: qsca.competitorGraphData.length }) }}
            </div>
            <div class="competitorCharts__charts">
              <template v-for="(storeData,storeKey) in qscas">
                <div v-for="(data,key) in storeData.competitorGraphData"
                     :key="'competitorQsca_' + key + 'store' + storeKey"
                     class="competitorChart"
                >
                  <ChartRadar
                    :chart-data="competitorChartData(storeData,data)"
                    :options="chartOptions"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #table>
      <CompaniesAnalysisTable
        :display-checkbox="false"
        :loading="loading"
        :items="qscas"
        :static-heads="dataTypes"
        :cross-search-fields="crossSearchFields"
        :competitor-mode="competitorMode"
        :value-formatter="valueFormatter"
        :activate-click-item-function="true"
        @click-item="openSentenceModal"
      />
      <KkSentencesModal
        v-model:is-visible="isVisibleSentencesModal"
        :positive="positiveSentences"
        :negative="negativeSentences"
        :qsca-category="qscaCategory"
        :is-loading="isSentencesLoading"
      />
    </template>
    <template #reviews>
      <Loading v-show="loading" />
      <div v-if="!loading" class="p-modalLikeBox" :class="{'p-modalLikeBox--competitor': showCompetitorReviews}">
        <div class="p-modalLikeBox__header">
          <button class="p-modalLikeBox__header__text" @click="switchCompetitorReviews">
            {{ $t('pages.companies.analysis.qscas.storeReviews') }}
          </button>
          <button v-if="competitorMode" class="competitorReviewTitle" @click="switchCompetitorReviews">
            {{ $t('pages.companies.analysis.qscas.competitorReviews') }}
          </button>
        </div>
        <div class="p-modalLikeBox__body">
          <div class="p-table__content">
            <div v-if="providedAiReviewSentencesAnalysis" class="p-box__info">
              <div class="c-info">
                <div class="c-info__icon">
                  <Icon name="iconQuestion" />
                </div>
                <div class="c-info__content">
                  <p>{{ $t('pages.companies.analysis.qscas.aiReviewDescription') }}</p>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th v-if="!providedAiReviewSentencesAnalysis">
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.keyword') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.store') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.rate') }}
                  </th>
                  <th>
                    {{ $t('pages.companies.analysis.qscas.reviewsTable.detail') }}
                  </th>
                </tr>
                <tr class="p-table__content__space"><td colspan="4" /></tr>
              </thead>
              <tbody>
                <tr v-if="displayReviews.length === 0">
                  <td colspan="4">{{ $t('messages.noData') }}</td>
                </tr>
                <tr v-for="(review,i) in displayReviews" v-else :key="'review_' + i">
                  <td v-if="!providedAiReviewSentencesAnalysis">
                    {{ review.keywords.join(',') }}
                  </td>
                  <td class="reviewStoreName">
                    {{ review.storeName }}
                  </td>
                  <td>
                    <div class="p-reviews__summary">
                      <div class="p-reviews__site">
                        <div class="p-reviews__site__icon">
                          <KkSiteIcon :name="review.siteType" />
                        </div>
                        <div
                          class="p-reviews__site__name"
                          data-test="review_item_site_name"
                          v-text="review.siteName"
                        />
                      </div>
                      <div class="p-reviews__reviewRating">
                        <div class="c-rating">
                          <div class="c-rating__number">
                            <template v-if="review.rating">
                              {{ review.rating }}
                            </template>
                            <template v-else>
                              -
                            </template>
                          </div>
                          <Star
                            v-show="review.rating"
                            :rating="review.rating"
                          />
                        </div>
                      </div>
                      <div
                        class="p-reviews__postedDate"
                        v-text="formatDate(review.postedAt)"
                      />
                      <div
                        class="p-reviews__reviewer"
                        v-text="review.reviewerName"
                      />
                    </div>
                  </td>
                  <td v-if="!providedAiReviewSentencesAnalysis" v-html="comment({text: sanitize(review.comment), keywords: review.keywords})" />
                  <td v-if="providedAiReviewSentencesAnalysis">
                    <p v-for="(sentence, j) in review.reviewSentences" :key="'sentence_' + j">
                      {{ sentence.category === "UNCLASSIFIED" ? "" : `[${sentence.category[0]}]` }}
                      <span :class="[ sentence.category === 'UNCLASSIFIED' ? '' : 'qscaReview', sentence.sentiment.toLowerCase() + 'Review' ]">{{ sentence.sentence }}</span>
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </CompaniesAnalysisLayout>
</template>
<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.chart {
  width: 30%;
  margin: 0 42px;
}
.p-modalLikeBox {
  margin-top: 48px;
  &__header {
    display: flex;
  }
  &__header__text {
    cursor: default;
  }
  &__body {
    border-top: 1px solid #E4E7EB;
    padding: 32px;
  }
  &--competitor {
    background: $color-competitor-bg;
    .competitorReviewTitle {
      color: $color-font-reverse;
      font-weight: bold;
      cursor: default;
    }
    .p-modalLikeBox__header__text {
      color: $color-font-gloomy;
      font-weight: normal;
      cursor: pointer;
    }
  }
}
:deep(.chartArea__header) {
  display: flex;
}
:deep(.chartArea__main) {
  display: flex;
}
.competitorCharts {
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  width: 70%;
  padding: 24px 32px;
  &__header {
    font-size: 18px;
    font-weight: bold;
    display: flex;
  }
  &__charts {
    display: flex;
    flex-shrink: 0;
    overflow: auto;
  }
}
.competitorChart {
  min-width: 35%;
  width: 35%;
  margin-right: 42px;
}
.competitorReviewTitle {
  color: $color-accent-primary;
  padding: 16px 0;
}
:deep(.highlight) {
  color: $color-accent-primary;
}
.reviewStoreName {
  width: 15%;
}
.qscaReview {
  font-weight: bold;
}
.positiveReview {
  color: $complete-color;
}
.negativeReview {
  color: $color-accent-primary;
}
</style>
